import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { theme as baseTheme } from '../styles/theme';
import settingsService from '../services/settingsService';
import LogoService from '../services/LogoService';
import { useAuth } from './AuthContext';
import { resolveTenantId, resolveCompanyLogo } from '../utils/tenantIdentity';
import { normalizeAppearanceSettings, resolveThemePreset, resolveBrandFontFamily } from '../config/appearanceConfig';
import { normalizeNavigationSettings } from '../config/navigationConfig';

const ThemeContext = createContext();
const FALLBACK = { primaryColor:'#1a5fa8', secondaryColor:'#00b8e6', accentColor:'#ff9800', fontFamily:'inter', fontScale:1 };

const hexToRgb = (hex) => {
  const raw=String(hex||'').replace('#','');
  const full=raw.length===3?raw.split('').map(c=>c+c).join(''):raw;
  const x=parseInt(full,16);
  if(!Number.isFinite(x)||full.length!==6)return {r:26,g:95,b:168};
  return {r:(x>>16)&255,g:(x>>8)&255,b:x&255};
};
const shade=(hex,factor=.72)=>{const {r,g,b}=hexToRgb(hex);return `rgb(${Math.round(r*factor)},${Math.round(g*factor)},${Math.round(b*factor)})`;};

const buildTheme=(branding={})=>{
  const primary=branding.primaryColor||FALLBACK.primaryColor;
  const secondary=branding.secondaryColor||FALLBACK.secondaryColor;
  const accent=branding.accentColor||FALLBACK.accentColor;
  return {
    ...baseTheme,
    branding:{primaryColor:primary,secondaryColor:secondary,accentColor:accent},
    colors:{
      ...baseTheme.colors,
      primary:{...baseTheme.colors.primary,500:primary,700:shade(primary,.68)},
      secondary:{...baseTheme.colors.secondary,500:secondary,700:shade(secondary,.68)},
      accent:{...baseTheme.colors.accent,500:accent,700:shade(accent,.72)},
      gradients:{...baseTheme.colors.gradients,primary:`linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`,secondary:`linear-gradient(135deg, ${secondary} 0%, ${primary} 100%)`,accent:`linear-gradient(135deg, ${accent} 0%, ${secondary} 100%)`}
    },
    shadows:{...baseTheme.shadows,glow:`0 0 40px ${primary}4d`}
  };
};

export const ThemeProvider = ({ children }) => {
  const { userData } = useAuth();
  const tenantId=resolveTenantId(userData);
  const [settings,setSettings]=useState(null);
  const [resolvedLogo,setResolvedLogo]=useState('');
  const [branding,setBranding]=useState(FALLBACK);
  const [loading,setLoading]=useState(Boolean(tenantId));

  useEffect(()=>{
    let active=true;
    const apply=async()=>{
      if(!tenantId){setSettings(null);setResolvedLogo('');setBranding({...FALLBACK});setLoading(false);return;}
      try{
        setLoading(true);
        const nextSettings=await settingsService.getTenantSettings(tenantId);
        const directLogo=resolveCompanyLogo(nextSettings||{},userData);
        const logo=directLogo||await LogoService.getLogo(tenantId)||'';
        if(!active)return;
        setSettings(nextSettings||{});
        setResolvedLogo(logo);
        setBranding({...FALLBACK,...(nextSettings?.branding||{})});
      }catch(error){
        console.warn('Theme settings could not be loaded:',error);
        if(active){
          setSettings(null);
          setResolvedLogo(resolveCompanyLogo({},userData));
          setBranding({...FALLBACK});
        }
      }finally{if(active)setLoading(false);}
    };
    apply();
    const refresh=(event)=>{
      const detail=event?.detail||{};
      if(detail.tenantId&&String(detail.tenantId)!==String(tenantId))return;
      const hasInlineUpdate = Boolean(detail.branding || detail.appearanceSettings || detail.dashboardSettings);
      if(hasInlineUpdate){
        if(detail.branding)setBranding(prev=>({...prev,...detail.branding}));
        setSettings(prev=>({
          ...(prev||{}),
          ...(detail.branding?{branding:{...(prev?.branding||{}),...detail.branding}}:{}),
          ...(detail.appearanceSettings?{appearanceSettings:normalizeAppearanceSettings(detail.appearanceSettings)}:{}),
          ...(detail.dashboardSettings?{dashboardSettings:detail.dashboardSettings}:{})
        }));
      } else apply();
    };
    const refreshLogo=(event)=>{
      const detail=event?.detail||{};
      if(detail.tenantId&&String(detail.tenantId)!==String(tenantId))return;
      const logoUrl=detail.logoUrl||detail.logo||'';
      setSettings(prev=>({
        ...(prev||{}),
        ...(detail.operationalSettings?{operationalSettings:detail.operationalSettings}:{}),
        ...(detail.taxSettings?{taxSettings:detail.taxSettings}:{}),
        ...(detail.workflowSettings?{workflowSettings:detail.workflowSettings}:{}),
        ...(detail.branding?{branding:{...(prev?.branding||{}),...detail.branding}}:{}),
        ...(detail.appearanceSettings?{appearanceSettings:normalizeAppearanceSettings(detail.appearanceSettings)}:{}),
        ...(detail.dashboardSettings?{dashboardSettings:detail.dashboardSettings}:{}),
        general:{...(prev?.general||{}),...(logoUrl?{logo:logoUrl,logoUrl}:{})}
      }));
      if(detail.branding)setBranding(prev=>({...prev,...detail.branding}));
      if(logoUrl){ setResolvedLogo(logoUrl); LogoService.cacheLogo(tenantId,logoUrl); }
      else if(!detail.operationalSettings&&!detail.taxSettings&&!detail.workflowSettings&&!detail.branding&&!detail.appearanceSettings&&!detail.dashboardSettings) apply();
    };
    window.addEventListener('cold-storage-theme-updated',refresh);
    window.addEventListener('cold-storage-company-settings-updated',refreshLogo);
    window.addEventListener('company-logo-updated',refreshLogo);
    return()=>{
      active=false;
      window.removeEventListener('cold-storage-theme-updated',refresh);
      window.removeEventListener('cold-storage-company-settings-updated',refreshLogo);
      window.removeEventListener('company-logo-updated',refreshLogo);
    };
  },[tenantId]);

  useEffect(()=>{
    const root=document.documentElement;
    const p=branding.primaryColor||FALLBACK.primaryColor;
    const s=branding.secondaryColor||FALLBACK.secondaryColor;
    const a=branding.accentColor||FALLBACK.accentColor;
    root.style.setProperty('--color-primary',p);
    root.style.setProperty('--color-primary-dark',shade(p,.68));
    root.style.setProperty('--color-primary-light',p);
    root.style.setProperty('--color-secondary',s);
    root.style.setProperty('--color-accent',a);
    const pr=hexToRgb(p);
    const sr=hexToRgb(s);
    const ar=hexToRgb(a);
    root.style.setProperty('--erp-primary',p);
    root.style.setProperty('--erp-secondary',s);
    root.style.setProperty('--erp-accent',a);
    root.style.setProperty('--erp-primary-rgb',`${pr.r}, ${pr.g}, ${pr.b}`);
    root.style.setProperty('--erp-secondary-rgb',`${sr.r}, ${sr.g}, ${sr.b}`);
    root.style.setProperty('--erp-accent-rgb',`${ar.r}, ${ar.g}, ${ar.b}`);

    const appearance = normalizeAppearanceSettings(settings?.appearanceSettings || settings?.settings?.appearanceSettings || {});
    const preset = resolveThemePreset(appearance);
    const radius = appearance.cardRadius == null ? preset.radius : Number(appearance.cardRadius || preset.radius);
    root.dataset.erpTheme = appearance.themeId;
    root.dataset.erpThemeMode = preset.mode || 'light';
    root.dataset.erpSurface = preset.surface || 'glass';
    root.dataset.erpDensity = appearance.density || preset.density || 'comfortable';
    const resolvedFontFamily = resolveBrandFontFamily(branding.fontFamily || preset.fontFamily || 'inter');
    const resolvedFontScale = Math.min(1.3, Math.max(0.85, Number(branding.fontScale || appearance.fontScale || 1) || 1));
    root.style.setProperty('--erp-font-family', resolvedFontFamily);
    root.style.setProperty('--erp-font-scale', String(resolvedFontScale));
    // Keep a real root typography scale for rem/inherited text while preserving
    // existing pixel-based operational layouts. This is deliberately bounded so
    // changing typography never breaks data-entry screens.
    root.style.fontSize = `${16 * resolvedFontScale}px`;
    root.style.setProperty('--erp-radius', `${Math.max(0, radius || 0)}px`);
    // The Modern Dashboard copies the reference layout, but every chromatic
    // surface is derived from tenant Branding. This keeps the visual system
    // consistent for every company and avoids hard-coded reference colors.
    const brandedModernBackground = `
      radial-gradient(circle at 7% 0%, rgba(${pr.r}, ${pr.g}, ${pr.b}, .11), transparent 29%),
      radial-gradient(circle at 92% 6%, rgba(${sr.r}, ${sr.g}, ${sr.b}, .095), transparent 26%),
      radial-gradient(circle at 86% 94%, rgba(${ar.r}, ${ar.g}, ${ar.b}, .075), transparent 25%),
      linear-gradient(145deg, rgba(${pr.r}, ${pr.g}, ${pr.b}, .032), #f8fafc 46%, rgba(${sr.r}, ${sr.g}, ${sr.b}, .025))
    `.replace(/\s+/g, ' ').trim();
    root.style.setProperty('--erp-page-bg', ['modern_dashboard','basic_erp'].includes(appearance.themeId) ? brandedModernBackground : (preset.pageBackground || '#f8fafc'));
    root.style.setProperty('--erp-card-bg', preset.cardBackground || '#fff');
    root.style.setProperty('--erp-card-border', preset.border || '1px solid rgba(148,163,184,.2)');
    root.style.setProperty('--erp-card-shadow', preset.shadow || 'none');
    root.style.setProperty('--erp-motion-factor', String(appearance.animationIntensity || 0));
    root.style.setProperty('--erp-theme-shadow-intensity', String(appearance.shadowIntensity || 0));
    root.style.setProperty('--erp-theme-glass-intensity', String(appearance.glassIntensity || 0));
    root.style.setProperty('--erp-theme-clay-intensity', String(appearance.clayIntensity || 0));

    const dark = preset.mode === 'dark';
    root.style.colorScheme = dark ? 'dark' : 'light';
    root.style.setProperty('--erp-text', dark ? '#e5edf8' : '#0f172a');
    root.style.setProperty('--erp-text-muted', dark ? '#91a2ba' : '#64748b');
    root.style.setProperty('--erp-nav-item-bg', dark ? 'rgba(15,26,44,.88)' : (preset.surface === 'flat' ? '#fff' : 'rgba(255,255,255,.76)'));
    root.style.setProperty('--erp-nav-border', preset.border || '1px solid rgba(148,163,184,.18)');
    root.style.setProperty('--erp-nav-radius', `${Math.max(0, Math.round((radius || 12) * .72))}px`);
    root.style.setProperty('--erp-dock-radius', preset.dockStyle === 'contrast' ? '0px' : preset.dockStyle === 'terminal' ? '6px' : preset.dockStyle === 'bubble' ? '34px' : `${Math.max(12, (radius || 18) + 4)}px`);
    root.style.setProperty('--erp-dock-icon-radius', preset.dockStyle === 'flat' || preset.dockStyle === 'contrast' ? '4px' : preset.dockStyle === 'bubble' ? '18px' : '13px');
    root.style.setProperty('--erp-dock-bg', dark ? (preset.dockStyle === 'black' ? '#030303' : 'rgba(8,18,32,.90)') : (preset.dockStyle === 'clay' ? '#eaf0f8' : preset.dockStyle === 'flat' ? '#fff' : 'rgba(255,255,255,.80)'));
    root.style.setProperty('--erp-dock-border', preset.border || '1px solid rgba(255,255,255,.84)');
    root.style.setProperty('--erp-dock-shadow', preset.dockStyle === 'clay' ? preset.shadow : preset.shadow || 'none');
    root.style.setProperty('--erp-dock-blur', ['crystal','glass'].includes(preset.surface) ? 'blur(24px) saturate(175%)' : 'none');
    document.body.dataset.tenantTheme='settings-service';
    document.body.style.fontFamily = 'var(--erp-font-family)';
    document.body.style.background = 'var(--erp-page-bg)';
    document.body.style.color = 'var(--erp-text)';
  },[branding, settings]);

  const companyLogo=resolvedLogo||resolveCompanyLogo(settings||{},userData);

  useEffect(()=>{
    const root=document.documentElement;
    if(companyLogo){
      const escaped=String(companyLogo).replace(/\\/g,'\\\\').replace(/"/g,'\\"');
      root.style.setProperty('--erp-company-logo',`url("${escaped}")`);
      root.dataset.companyLogo='configured';
    }else{
      root.style.removeProperty('--erp-company-logo');
      delete root.dataset.companyLogo;
    }
  },[companyLogo]);

  const currentTheme=useMemo(()=>buildTheme(branding),[branding]);
  const appearanceSettings=useMemo(()=>normalizeAppearanceSettings(settings?.appearanceSettings || settings?.settings?.appearanceSettings || {}),[settings]);
  const navigationSettings=useMemo(()=>normalizeNavigationSettings(settings?.navigationSettings || settings?.settings?.navigationSettings || {}),[settings]);
  const companyName=settings?.general?.companyName||userData?.companyName||userData?.tenantName||'';
  return <ThemeContext.Provider value={{theme:currentTheme,branding,settings,appearanceSettings,navigationSettings,companyLogo,companyName,tenantId,loading,refreshTheme:async()=>{
    if(!tenantId)return;
    const nextSettings=await settingsService.getTenantSettings(tenantId);
    const directLogo=resolveCompanyLogo(nextSettings||{},userData);
    const logo=directLogo||await LogoService.getLogo(tenantId)||'';
    setSettings(nextSettings||{});
    setResolvedLogo(logo);
    setBranding({...FALLBACK,...(nextSettings?.branding||{})});
  }}}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
};

export default ThemeContext;
